/**
 * `JSON.stringify` that produces the same string for two logically identical
 * values: object keys are sorted at every level and `undefined` properties are
 * dropped, so `{ a: 1, b: undefined }` and `{ b: undefined, a: 1 }` and
 * `{ a: 1 }` all serialize alike.
 *
 * Cache keys are compared as strings, so a key that depends on property order
 * turns a logically identical lookup into a miss — an extra PDP request rather
 * than a wrong answer, but one that is invisible until someone profiles it.
 * Array order is preserved: it carries meaning in general, and callers that
 * know it does not (a set of actions) sort before serializing.
 */
export const canonicalJson = (value: unknown): string => JSON.stringify(canonicalize(value));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }

  if (isRecord(value)) {
    const entries = Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : 1));

    return Object.fromEntries(entries.map(([key, entry]) => [key, canonicalize(entry)]));
  }

  return value;
};
